import type { Controls } from "@obean/game-contract";
import { controlsFromKeys, isGameKey } from "./controls.js";

/** A text field, text area or editable element has the key: the player is typing. */
function typing(target: EventTarget | null): boolean {
  const el = target as { tagName?: unknown; isContentEditable?: unknown } | null;
  if (!el) return false;
  return (
    el.isContentEditable === true ||
    (typeof el.tagName === "string" && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))
  );
}

/**
 * Held game keys, in press order. Everything is released when the page loses focus. A key pressed
 * and let go between two samples still counts once, in the next sample: games read input a few
 * dozen times a second, and a quick tap (a turn in a puzzle) must not fall between two reads.
 */
export class KeyboardInput {
  #held: string[] = [];
  /** Keys pressed since the last sample. */
  #tapped: string[] = [];
  readonly #target: Window;

  constructor(target: Window = window) {
    this.#target = target;
    target.addEventListener("keydown", this.#down);
    target.addEventListener("keyup", this.#up);
    target.addEventListener("blur", this.#releaseAll);
    target.document.addEventListener("visibilitychange", this.#releaseAll);
  }

  readonly #down = (e: KeyboardEvent) => {
    // Typing in a game's own text field (Beanstorm's answers): its keys are letters, not moves.
    if (!isGameKey(e.code) || typing(e.target)) return;
    e.preventDefault(); // no page scrolling on arrows or Space
    this.#held = [...this.#held.filter((k) => k !== e.code), e.code];
    if (!this.#tapped.includes(e.code)) this.#tapped.push(e.code);
  };

  readonly #up = (e: KeyboardEvent) => {
    this.#held = this.#held.filter((k) => k !== e.code);
  };

  readonly #releaseAll = () => {
    this.#held = [];
    this.#tapped = [];
  };

  sample(): Controls {
    const quick = this.#tapped.filter((k) => !this.#held.includes(k));
    this.#tapped = [];
    return controlsFromKeys([...this.#held, ...quick]);
  }

  dispose(): void {
    this.#target.removeEventListener("keydown", this.#down);
    this.#target.removeEventListener("keyup", this.#up);
    this.#target.removeEventListener("blur", this.#releaseAll);
    this.#target.document.removeEventListener("visibilitychange", this.#releaseAll);
  }
}
